import React from "react";
import { motion } from "framer-motion";
import { Clock, MessageSquareWarning, AlertCircle, Users } from "lucide-react";

/**
 * PainPointsAnimatedSection
 * -----------------------------------------------------
 * A meaningful animation that visualises a patient's journey through the system.
 * Dots (patients) travel along a path through four stages (cards). At each stage,
 * we apply a subtle micro-interaction that matches the problem:
 *  1) Unnecessary Delays → slow clock sweep + queue builds briefly
 *  2) Feeling Unheard → speech bubble jitter indicating missed comms
 *  3) Critical Gaps → warning pulse; the path narrows (bottleneck)
 *  4) Vulnerable Populations → protective ring expands, hinting who bears the load
 * Motion-safe: honours prefers-reduced-motion by swapping to static highlights.
 * Tailwind: uses your CSS variables & the expanded animation utilities I provided.
 */

// Helper functions for consistent icon theming
const getIconBgColor = (index: number) => {
  const colors = [
    'bg-red-100 dark:bg-red-900/30',
    'bg-orange-100 dark:bg-orange-900/30',
    'bg-amber-100 dark:bg-amber-900/30',
    'bg-rose-100 dark:bg-rose-900/30',
  ];
  return colors[index % colors.length];
};

const getIconColor = (index: number) => {
  const colors = [
    'text-red-600 dark:text-red-400',
    'text-orange-600 dark:text-orange-400',
    'text-amber-600 dark:text-amber-400',
    'text-rose-600 dark:text-rose-400',
  ];
  return colors[index % colors.length];
};

const items = [
  {
    id: 1,
    title: "Unnecessary Delays",
    body: "Many Australians face prolonged waiting times and inefficient healthcare processes that delay critical care and treatment.",
    Icon: Clock,
    color: "ring-red-500/20",
  },
  {
    id: 2,
    title: "Feeling Unheard",
    body: "Patients and carers often struggle to communicate their concerns effectively, leading to misdiagnosis and frustration.",
    Icon: MessageSquareWarning,
    color: "ring-orange-500/20",
  },
  {
    id: 3,
    title: "Critical Gaps",
    body: "Emergency departments at capacity and under-resourced clinics create dangerous gaps in care when people need it most.",
    Icon: AlertCircle,
    color: "ring-amber-500/20",
  },
  {
    id: 4,
    title: "Vulnerable Populations",
    body: "The most vulnerable among us—children, elderly, and those with disabilities—are disproportionately affected by system failures.",
    Icon: Users,
    color: "ring-rose-500/20",
  },
];

const usePrefersReducedMotion = () => {
  const [reduce, setReduce] = React.useState(false);
  React.useEffect(() => {
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduce(m.matches);
    const onChange = () => setReduce(m.matches);
    m.addEventListener?.("change", onChange);
    return () => m.removeEventListener?.("change", onChange);
  }, []);
  return reduce;
};

const PatientDot: React.FC<{ delay: number }>=({ delay })=>{
  const reduce = usePrefersReducedMotion();
  if (reduce) return null;
  return (
    <motion.span
      className="absolute h-2 w-2 rounded-full bg-white dark:bg-black shadow-[0_0_0_4px_hsl(var(--primary)/.2)]"
      initial={{ left: 0, opacity: 0 }}
      animate={{
        opacity: [0,1,1,0],
        left: [0, "25%", "50%", "75%", "100%"],
      }}
      transition={{ duration: 10, delay, ease: "easeInOut", repeat: Infinity }}
      aria-hidden="true"
    />
  );
};

const StageMicroFX: React.FC<{ index: number }>=({ index })=>{
  const reduce = usePrefersReducedMotion();
  if (reduce) return null;

  // Each stage gets a subtle, meaningful micro-animation
  switch (index) {
    case 0: // Delays → slow rotating sweep overlay
      return (
        <motion.div
          className="absolute -inset-[2px] rounded-2xl"
          style={{
            background:
              "conic-gradient(from 0deg, transparent 0deg, hsl(var(--primary)/.22) 70deg, transparent 120deg)",
            maskImage:
              "radial-gradient(80% 80% at 50% 50%, black, transparent)",
            WebkitMaskImage:
              "radial-gradient(80% 80% at 50% 50%, black, transparent)"
          }}
          animate={{ rotate: 360 }}
          transition={{ duration: 14, repeat: Infinity, ease: "linear" }}
        />
      );

    case 1: // Unheard → subtle plane jitter + faint highlight swipe
      return (
        <motion.div
          className="absolute inset-0 rounded-2xl"
          style={{
            background:
              "linear-gradient(135deg, transparent 60%, rgba(255,255,255,.06) 70%, transparent 80%)"
          }}
          animate={{
            x: [0, 1.2, -1.2, 0.8, -0.4, 0],
            y: [0, 0.4, -0.4, 0, 0.2, 0]
          }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        />
      );

    case 2: // Critical gaps → periodic warning pulse
      return (
        <>
          <motion.div
            className="absolute inset-0 rounded-2xl"
            animate={{
              boxShadow: [
                "0 0 0 0 rgba(239,68,68,0)",
                "0 0 0 18px rgba(239,68,68,.12)",
                "0 0 0 0 rgba(239,68,68,0)"
              ]
            }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute inset-0 rounded-2xl ring-2 ring-red-500/0"
            animate={{ opacity: [0, 0.85, 0], scale: [0.985, 1, 1] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          />
        </>
      );

    case 3: // Vulnerable populations → protective halo grows
      return (
        <>
          <motion.div
            className="absolute -inset-3 rounded-[1.4rem]"
            style={{
              background:
                "radial-gradient(60% 60% at 50% 50%, hsl(var(--primary)/.18), transparent 70%)",
              filter: "blur(8px)"
            }}
            animate={{ opacity: [0, 0.9, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute inset-0 rounded-2xl border"
            animate={{
              borderColor: [
                "rgba(255,255,255,0)",
                "hsl(var(--primary)/.5)",
                "rgba(255,255,255,0)"
              ]
            }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          />
        </>
      );
    default:
      return null;
    }
  };

const Card: React.FC<{
  title: string;
  body: string;
  Icon: React.ComponentType<any>;
  accent: string;
  index: number;
}> = ({ title, body, Icon, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay: 0.1 * index }}
      className="relative group"
    >
      <div className={`relative h-full bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden ${index === items.length - 1 ? 'lg:col-span-2 lg:flex-row' : ''}`}>
        <div className="absolute inset-0 bg-gradient-to-br from-white/50 to-transparent dark:from-gray-800/20" />
        <StageMicroFX index={index} />
        <div className="relative p-6 h-full flex flex-col">
          <div className="flex items-start space-x-4 mb-4">
            <div className={`p-3 rounded-lg ${getIconBgColor(index)}`}>
              <Icon className={`h-6 w-6 ${getIconColor(index)}`} aria-hidden="true" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mt-1">
              {title}
            </h3>
          </div>
          <p className="text-gray-600 dark:text-gray-300 text-base leading-relaxed flex-grow">
            {body}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default function UnmetNeedSection() {
  const reduce = usePrefersReducedMotion();
  return (
    <section className="py-20 bg-white dark:bg-black">
      <div className="container mx-auto px-6 max-w-7xl">
        <header className="mb-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            What People Experience Today
          </h2>
          <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            A quick journey through the healthcare system, and where it breaks down for those who need it most.
          </p>
        </header>

        {/* Journey line (patients move left → right) */}
        <div className="relative mb-8 h-16 sm:h-20">
          <div className="absolute top-1/2 left-0 right-0 -translate-y-1/2 h-[3px] rounded-full bg-gradient-to-r from-muted via-primary/20 to-muted" />
          {/* Stations under each card position */}
          <div className="absolute inset-0 flex items-center justify-between px-2 rounded-lg">
            {items.map((_, i) => (
              <div key={i} className="relative h-3 w-3 rounded-full bg-primary/60 shadow-elevita">
                {!reduce && (
                  <span className="absolute -inset-2 rounded-full ring-2 ring-primary/20 animate-soft-pulse"/>
                )}
              </div>
            ))}
          </div>
          {/* Patient dots travelling */}
          {!reduce && (
            <>
              <PatientDot delay={0} />
              <PatientDot delay={2.5} />
              <PatientDot delay={5} />
            </>
          )}
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {items.map((it, idx) => (
            <Card
              key={it.id}
              title={it.title}
              body={it.body}
              Icon={it.Icon}
              accent={it.color}
              index={idx}
            />
          ))}
        </div>

        {/* Quote / CTA band */}
        <motion.div 
          className="mt-16 bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/30 dark:to-cyan-900/20 rounded-2xl p-8 md:p-10 shadow-sm border border-teal-100 dark:border-teal-900/50"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <blockquote className="max-w-3xl mx-auto text-center">
            <p className="text-xl md:text-2xl font-medium text-gray-800 dark:text-gray-200 italic leading-relaxed">
              "A system that can unintentionally fall short for those who need support most."
            </p>
            <footer className="mt-4 text-teal-600 dark:text-teal-400 font-medium">
              — Real patient experience
            </footer>
          </blockquote>
        </motion.div>
      </div>
    </section>
  );
}